import {
  IconSparkles,
  IconMessage,
  IconApi,
  IconVariable,
  IconForms,
  IconBrandWhatsapp,
  IconMail,
  IconPhoto,
  IconHeadset,
  IconBrain,
  IconRoute,
  IconClock,
  IconDatabase,
  IconCalendarEvent,
  IconCalendarSearch,
  IconCalendarOff,
  IconCalendarTime,
  IconCalendarCheck,
  IconBook,
  IconClipboardList,
  IconShoppingCart,
  IconEye,
  IconMailSearch,
  IconFileSpreadsheet,
  IconFlag,
} from '@tabler/icons-react';

import { CAPABILITIES, SCHEDULING_FIELD_OPTIONS, MEDIA_ANALYSIS_PRESETS } from '../../constants';

// =============================================================================
// NODE TEMPLATES
// =============================================================================
export const NODE_CATEGORIES = [
  // 1º - Ações
  {
    id: 'actions',
    label: 'Ações',
    description: 'Executar tarefas',
    infoTitle: 'O que são Ações?',
    infoContent: `
**Ações são ordens diretas que o agente executa, uma por vez, em sequência.**

Pense assim: é como dar instruções passo a passo para alguém.
- "Diga bom dia" → o agente diz bom dia
- "Pergunte o nome" → o agente pergunta o nome
- "Envie um email" → o agente envia o email

Cada ação faz **uma coisa só** e depois segue para a próxima.

### Quando usar?

- **Mensagem com IA** — Quando você quer que a IA responda de forma inteligente com base em instruções suas
- **Mensagem** — Para enviar um texto fixo (ex: "Olá! Bem-vindo à nossa loja")
- **Coletar Dado** — Para pedir uma informação específica ao cliente (nome, email, CPF)
- **WhatsApp / Email** — Para enviar notificações ou confirmações
- **API** — Para conectar com sistemas externos (consultar estoque, criar pedido)
- **Variável** — Para salvar um valor e reutilizar depois no fluxo
- **Enviar Mídia** — Para enviar uma imagem, vídeo ou documento da sua galeria

### Exemplo prático

Imagine uma pizzaria:
1. **Mensagem** → "Olá! Qual sabor você deseja?"
2. **Coletar Dado** → Coleta o sabor escolhido
3. **Mensagem com IA** → IA confirma o pedido de forma natural
4. **WhatsApp** → Envia confirmação para o cliente
    `,
    items: [
       {
        key: 'ai_response',
        type: 'action',
        label: 'Mensagem com IA',
        description: 'Resposta gerada por IA',
        icon: IconSparkles,
        gradient: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
        config: {
          instructions: '',
          use_knowledge_base: true,
          use_conversation_history: true,
          wait_for_response: true,
          save_response_as: 'user_response',
          extract_variables: [],
        },
      },
      {
        key: 'send_message',
        type: 'action',
        label: 'Mensagem',
        description: 'Resposta pré definida',
        icon: IconMessage,
        gradient: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
        config: { message: '', wait_for_response: true, save_response_as: 'user_response' },
      },
      {
        key: 'api_call',
        type: 'action',
        label: 'API',
        description: 'Chamar API externa',
        icon: IconApi,
        gradient: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)',
        config: { endpoint: '', method: 'GET' },
        pro: true,
      },
      

      {
        key: 'collect_field',
        type: 'action',
        label: 'Coletar Dado',
        description: 'Coletar e salvar no CRM',
        icon: IconForms,
        gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        config: {
          field_name: '',
          prompt: '',
          validation: 'none',
          validation_options: '',
          required: true,
          sync_crm: true,
          crm_field: '',
          wait_for_response: true,
        },
      },
      {
        key: 'send_whatsapp',
        type: 'action',
        label: 'WhatsApp',
        description: 'Enviar WhatsApp',
        icon: IconBrandWhatsapp,
        gradient: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)',
        config: {
          recipient_type: 'client',
          phones: '',
          message_type: 'fixed',
          message: '',
          ai_prompt: '',
          delay_value: 0,
          delay_unit: 'minutes',
        },
      },
      {
        key: 'send_email',
        type: 'action',
        label: 'Email',
        description: 'Enviar Email',
        icon: IconMail,
        gradient: 'linear-gradient(135deg, #ea4335 0%, #d93025 100%)',
        pro: true,
        config: {
          sender_name: '',
          recipient_type: 'client',
          to: '',
          cc: '',
          subject: '',
          message_type: 'ai',
          message: '',
          ai_prompt: 'Gere um email HTML profissional de confirmação para o cliente.\n\nUse os dados coletados na conversa (nome, telefone, data, horário).\nO email deve ter:\n- Saudação personalizada\n- Detalhes do agendamento/solicitação\n- Informações de contato para dúvidas\n- Assinatura profissional\n\nResponda APENAS com o HTML do email, sem explicações.',
          is_html: true,
          delay_value: 0,
          delay_unit: 'minutes',
        },
      },
      {
        key: 'send_media',
        type: 'action',
        label: 'Enviar mídia',
        description: 'Enviar mídia da galeria',
        icon: IconPhoto,
        gradient: 'linear-gradient(135deg, #f472b6 0%, #db2777 100%)',
        config: {
          mediaId: '',
          caption: '',
          providers: ['whatsapp'],
          delay_value: 0,
          delay_unit: 'minutes',
        },
      },
      {
        key: 'transfer_to_human',
        type: 'action',
        label: 'Transferir para Humano',
        description: 'Pausa a IA e chama um atendente',
        icon: IconHeadset,
        gradient: 'linear-gradient(135deg, #f43f5e 0%, #be123c 100%)',
        config: {
          message: 'Vou te transferir para um de nossos atendentes. Só um momento, por favor.',
          reason: 'client_request',
        },
      },
      {
        key: 'end',
        type: 'end',
        label: 'Fim',
        description: 'Encerrar fluxo',
        icon: IconFlag,
        gradient: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
        maxInstances: 1,
        maxInstancesMessage: 'Apenas 1 nó "Fim" é permitido por fluxo.',
      },
    ],
  },
  // 2º - Condições
  {
    id: 'conditions',
    label: 'Condições',
    description: 'Lógica de decisão',
    infoTitle: 'O que são Condições?',
    infoContent: `
**Condições são perguntas de "sim ou não" que criam caminhos diferentes no fluxo.**

Pense assim: é como uma bifurcação na estrada. Dependendo da resposta, o agente vai por um caminho ou outro.

### Como funcionam?

A condição verifica algo e decide para onde ir:
- "É horário comercial?" → **Sim:** atende normalmente / **Não:** manda mensagem de fora do expediente
- "O cliente quer agendar?" → **Sim:** vai para agendamento / **Não:** vai para FAQ
- "Já coletou o email?" → **Sim:** pula / **Não:** pede o email

### Quando usar?

- **Intenção** — Quando você quer que a IA entenda o que o cliente quer (ex: "quero agendar", "quero cancelar", "tenho uma dúvida"). A IA analisa a mensagem e direciona para o caminho certo
- **Horário** — Quando o comportamento muda conforme o horário (ex: dentro/fora do expediente, almoço, fim de semana)
- **Dados** — Quando você precisa verificar se já tem uma informação antes de pedir (ex: "já tem o nome do cliente?")

### Exemplo prático

Imagine um consultório:
1. Cliente manda mensagem
2. **Condição: Horário** → É horário de atendimento?
   - **Sim** → **Condição: Intenção** → O que o cliente quer?
     - "Agendar" → vai para agendamento
     - "Dúvida" → vai para FAQ
   - **Não** → Mensagem "Estamos fechados, retornaremos amanhã às 8h"
    `,
    items: [
      {
        key: 'intent',
        type: 'condition',
        label: 'Intenção',
        description: 'Detectar intenção do usuário',
        icon: IconBrain,
        gradient: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
      },
      {
        key: 'intent_router',
        type: 'condition',
        label: 'Roteador Inteligente',
        description: 'Rotear por múltiplas intenções',
        icon: IconRoute,
        gradient: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
        config: {
          intents: [],
          minConfidence: 60,
        },
      },
      {
        key: 'time',
        type: 'condition',
        label: 'Horário',
        description: 'Verificar horário atual',
        icon: IconClock,
        gradient: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
      },
      {
        key: 'data_check',
        type: 'condition',
        label: 'Dados',
        description: 'Verificar dados coletados',
        icon: IconDatabase,
        gradient: 'linear-gradient(135deg, #78716c 0%, #57534e 100%)',
      },

    ],
  },
  // 3º - Capacidades
  {
    id: 'capabilities',
    label: 'Capacidades',
    description: 'O que seu agente pode fazer',
    infoTitle: 'O que são Capacidades?',
    infoContent: `
**Capacidades são habilidades completas que a IA sabe fazer sozinha, sem precisar de instruções passo a passo.**

A diferença para Ações é simples:
- **Ação** = você diz "faça isso" → a IA faz exatamente aquilo
- **Capacidade** = você diz "saiba fazer isso" → a IA conduz toda a conversa sozinha

### Como funcionam?

Quando o fluxo chega em uma Capacidade, a IA assume o controle e gerencia toda a interação com o cliente. Ela sabe perguntar, responder, validar e concluir — tudo automaticamente.

### Quando usar?

- **Agendamento** — A IA conduz todo o processo: pergunta a data, mostra horários disponíveis, confirma, e até permite cancelar ou remarcar. Você não precisa criar cada passo
- **Verificar Disponibilidade** — O cliente quer saber se tem horário livre, mas sem agendar ainda
- **FAQ** — A IA responde perguntas usando sua base de conhecimento. Se não souber, usa a mensagem de fallback
- **Captura de Lead** — A IA coleta nome, telefone, email e salva no CRM automaticamente
- **Enviar Mídias** — A IA decide sozinha quando enviar uma foto ou documento relevante da sua galeria (ex: cardápio, tabela de preços)
- **Análise de Mídia** — O cliente envia uma foto ou documento e a IA analisa (ex: orçamento por foto, leitura de documentos)

### Exemplo prático

Imagine um salão de beleza:
1. Cliente diz "quero agendar um corte"
2. **Capacidade: Agendamento** → a IA sozinha:
   - Pergunta "Qual dia você prefere?"
   - Mostra os horários disponíveis
   - Pergunta "Com qual profissional?"
   - Confirma tudo e salva o agendamento
   - Envia mensagem de confirmação

Tudo isso com **um único bloco** no fluxo!
    `,
    items: [
      {
        key: 'scheduling',
        type: 'capability',
        label: 'Agendamento',
        description: 'Agendar, cancelar e remarcar',
        icon: IconCalendarEvent,
        gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        config: {
          custom_confirmation_message: 'Seu agendamento foi confirmado!\n\n📅 Data: {{date}}\n🕐 Horário: {{time}}\n👤 Profissional: {{professional}}\n\nAté logo!',
          post_scheduling_response: 'ai_response',
          cancellation_enabled: true,
          reschedule_enabled: true,
          lookup_field: 'phone',
        },
      },
      {
        key: 'check_availability',
        type: 'capability',
        label: 'Verificar Disponibilidade',
        description: 'Consultar horários sem agendar',
        icon: IconCalendarSearch,
        gradient: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)',
        config: {},
      },
      {
        key: 'reschedule',
        type: 'capability',
        label: 'Reagendamento',
        description: 'Remarcar agendamento existente (sem sinal)',
        icon: IconCalendarTime,
        gradient: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
        config: {
          lookup_field: 'phone',
          allow_same_day: true,
          max_advance_days: 30,
          max_slots_to_show: 5,
          calendar_provider: 'internal',
          post_reschedule_response: 'ai_response',
          custom_reschedule_message: 'Seu agendamento foi remarcado!\n\n📅 Nova data: {{date}}\n🕐 Novo horário: {{time}}\n👤 Profissional: {{professional}}',
        },
      },
      {
        key: 'cancellation',
        type: 'capability',
        label: 'Cancelamento',
        description: 'Cancelar agendamento existente',
        icon: IconCalendarOff,
        gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
        config: {
          lookup_field: 'phone',
          ask_cancellation_reason: false,
          min_advance_hours: 0,
          post_cancellation_response: 'ai_response',
          custom_cancellation_message: 'Seu agendamento foi cancelado.\n\nSe precisar marcar outro horário, é só me chamar! 😊',
        },
      },
      {
        key: 'appointment_confirmation',
        type: 'capability',
        label: 'Confirmação de Agendamento',
        description: 'Confirmar presença ou registrar cancelamento',
        icon: IconCalendarCheck,
        gradient: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
        config: {
          lookup_field: 'phone',
          ask_confirmation_reason: false,
        },
      },
      {
        key: 'faq',
        type: 'capability',
        label: 'Base de Conhecimento',
        description: 'Responder perguntas frequentes',
        icon: IconBook,
        gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
        config: { fallbackMessage: 'Não encontrei essa informação.', filter_categories: [] },
      },

      {
        key: 'data_collection',
        type: 'capability',
        label: 'Captura de Lead',
        description: 'Capturar informações do lead',
        icon: IconClipboardList,
        gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        // maxInstances removido: múltiplos nós de Captura de Lead são permitidos
        // para suportar coleta em diferentes pontos do grafo (início, fim, sub-agentes)
        config: {
          destination: 'internal_crm',
          required_fields: ['name', 'phone'],
          optional_fields: [],
          validate_fields: true,
          confirm_before_save: false,
        },
      },
      // { DESATIVADO POR ENQUANTO
      //   key: 'sales',
      //   type: 'capability',
      //   label: 'Vendas',
      //   description: 'Processar vendas',
      //   icon: IconShoppingCart,
      //   gradient: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
      //   config: { catalogEnabled: true },
      // },
      {
        key: 'media_library',
        type: 'capability',
        label: 'Enviar Mídias',
        description: 'IA envia mídias da galeria',
        icon: IconPhoto,
        gradient: 'linear-gradient(135deg, #f472b6 0%, #a855f7 100%)',
        config: {
          media_items: [],
          general_instruction: '',
          max_sends_per_conversation: 3,
        },
      },
      {
        key: 'media_analysis',
        type: 'capability',
        label: 'Análise de Mídia',
        description: 'Analisar imagens e documentos',
        icon: IconEye,
        gradient: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
        config: {
          prompt_preset: 'general',
          prompt_template: '',
          response_prompt: '',
          detail_level: 'high',
          max_pdf_pages: 1,
          require_media: true,
          no_media_message: 'Por favor, envie uma imagem ou documento para que eu possa analisar.',
          fallback_when_no_media: 'fixed_message',
        },
      },
      {
        key: 'email_monitor',
        type: 'capability',
        label: 'Leitor de Email',
        description: 'Ler, analisar e agir sobre emails do Gmail',
        icon: IconMailSearch,
        gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
        pro: true,
        config: {
          time_filter: '24h',
          unread_only: true,
          label_filter: 'all',
          sender_filter: '',
          subject_filter: '',
          auto_reply: false,
          reply_instructions: '',
          forward_to_whatsapp: false,
          whatsapp_number: '',
          whatsapp_summary: true,
          mark_as_read: false,
        },
      },
      {
        key: 'sheets_reader',
        type: 'capability',
        label: 'Leitor de Planilha',
        description: 'Analisar e conversar sobre dados do Google Sheets',
        icon: IconFileSpreadsheet,
        gradient: 'linear-gradient(135deg, #0f9d58 0%, #0b8043 100%)',
        pro: true,
        config: {
          spreadsheet_id: '',
          sheet_name: '',
          analysis_instructions: '',
        },
      },
    ],
  },
];
